import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';
import OutputText from '@ey/canvascoreservices/OutputText';
import MotifSearch from '../../../Common/Search/MotifSearch';
import CheckboxItem from './CheckboxItem';
import {labels} from '../../../../util/utils';
import Utility from '../../../../util/utilityfunctions';
import Button from '../../../CoreWrappers/Button';
import IconMessage from '../../../CoreWrappers/IconMessage';

const FiltersList = (props) => {
	const minLevel = 2;
	const {dashboardEntities, onCheckItem, onCheckAll, isGrouping, allEntities, level} = props;
	const [selectedAll, setSelectedAll] = useState(false);
	const [entities, setEntities] = useState([]);
	const [noResult, setNoResult] = useState(false);
	const [groupedEntities, setGroupedEntities] = useState(null);
	const [isSearching, setIsSearching] = useState(false);
	const [searchValue, setSearchValue] = useState('');

	useEffect(() => {
		if (dashboardEntities?.length > 0 && !isSearching) {
			if (dashboardEntities[0]?.entityLevel > 2) {
				groupEntitiesByParent(dashboardEntities);
			}
			setEntities(_.sortBy(dashboardEntities, 'label'));
			setSelectedAll(isEachEntitySelected);
		}
	}, [dashboardEntities]);

	const areValues = () => {
		return dashboardEntities?.length > 0;
	};

	const isEachEntitySelected = areValues()
		? dashboardEntities.filter(({checked}) => checked).length === dashboardEntities.length
		: false;

	const groupEntitiesByParent = (ent) => {
		const groupEntities = _.groupBy(ent, (entity) => entity.parentId);

		const groups = Object.keys(groupEntities).map((key) => {
			const parent = allEntities.find((e) => e.id === key);
			return {
				id: parent.id,
				label: parent.label,
				children: _.sortBy([...groupEntities[key]], 'label')
			};
		});

		setGroupedEntities(_.sortBy(groups, 'label'));
	};

	const handlerOnItemCheck = (value, isChecked) => {
		if (onCheckItem) {
			onCheckItem(value, isChecked);
		}
		setSelectedAll(isEachEntitySelected);
	};

	const handlerOnSelectAll = (selected) => {
		const selectedEntities = [
			...entities.map((item) => {
				return {...item, checked: !selected};
			})
		];

		setEntities(selectedEntities);
		setSelectedAll(isEachEntitySelected);

		if (onCheckAll) {
			onCheckAll(selectedEntities, !selected);
		}
	};

	const handlerOnSearch = (newValue) => {
		const searchList = [];
		setSearchValue(newValue);
		setNoResult(false);

		if (newValue.length > 0 && entities) {
			setIsSearching(true);
			dashboardEntities.forEach((entity) => {
				if (Utility.isTextIncluded(entity.label, newValue)) {
					searchList.push(entity);
				}
			});

			setNoResult(searchList.length === 0);
			setEntities(_.sortBy(searchList, 'label'));
			if (groupedEntities) {
				groupEntitiesByParent(searchList);
			}
		} else {
			setIsSearching(false);
			setEntities(_.sortBy(dashboardEntities, 'label'));
			if (dashboardEntities[0]?.entityLevel > minLevel) {
				groupEntitiesByParent(dashboardEntities);
			}
			setSelectedAll(isEachEntitySelected);
		}
	};

	const handlerOnClear = () => {
		setNoResult(false);
		setIsSearching(false);
		setEntities(_.sortBy(dashboardEntities, 'label'));
		if (dashboardEntities[0]?.entityLevel > minLevel) {
			groupEntitiesByParent(dashboardEntities);
		}
		setSelectedAll(isEachEntitySelected);
	};

	const propsMsg = {
		title: '',
		subTitle: labels.thereIsNoDataToDisplay
	};
	const center = classNames({center: dashboardEntities.length === 0});
	return (
		<StyledFiltersList className={(`level-${level}`, `${center}`)} length={dashboardEntities.length}>
			{dashboardEntities.length === 0 && <IconMessage {...propsMsg} />}
			{dashboardEntities.length > 0 && (
				<>
					<section className="listitem">
						<Button
							variant={'text-alt'}
							label={selectedAll ? labels.deselectAll : labels.selectAll}
							onClick={() => handlerOnSelectAll(selectedAll)}
							disabled={isGrouping ? !groupedEntities?.length : !entities?.length}
						/>
					</section>
					<MotifSearch
						value={searchValue}
						onChange={handlerOnSearch}
						onClear={handlerOnClear}
						placeholder={labels.search}
						searchHoverText={labels.search}
					/>
				</>
			)}
			{isGrouping &&
				groupedEntities &&
				groupedEntities.map((group) => {
					return (
						<div className="entities">
							<OutputText body2 title={group.label}>
								{group.label}
							</OutputText>
							<div>
								{group.children.map((eg) => {
									return <CheckboxItem key={eg.id} entity={eg} onCheckChange={handlerOnItemCheck} />;
								})}
							</div>
						</div>
					);
				})}
			{!isGrouping && entities && entities.length > 0 && (
				<div className="entities">
					{entities.map((entity) => {
						return <CheckboxItem key={entity.id} entity={entity} onCheckChange={handlerOnItemCheck} />;
					})}
				</div>
			)}

			{noResult && <div className="no-results-found">{labels.noResultsFound}</div>}
		</StyledFiltersList>
	);
};

FiltersList.propTypes = {
	dashboardEntities: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			count: PropTypes.number,
			level: PropTypes.number
		})
	),
	isGrouping: PropTypes.bool,
	onCheckItem: PropTypes.func,
	onCheckAll: PropTypes.func
};

export default FiltersList;

const StyledFiltersList = styled.section`
	display: flex;
	flex-direction: column;
	flex: 1;
	padding: var(--spacing-x-large) var(--spacing-large);
	border-right: 0.1rem solid ${(props) => props.theme.color[`grey300`]};
	gap: var(--spacing-x-large);
	&.center {
		justify-content: center;
	}
	.entities {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-small);
	}
`;
